// src/pages/Billing.jsx
import React, { useState, useEffect, useMemo } from 'react';
import './Billing.css';

const Billing = () => {
  const [activeTab, setActiveTab] = useState('plan');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [userInfo, setUserInfo] = useState({});
  
  // Datos de ejemplo
  const currentPlan = {
    name: "Plan Empresarial",
    price: "€99.00",
    billingCycle: "mensual",
    nextBilling: "15/05/2025",
    features: [
      "Hasta 20 bots activos",
      "Conexiones ilimitadas",
      "Soporte prioritario",
      "Analíticas avanzadas",
      "API personalizada"
    ]
  };
  
  const invoices = [
    { id: 'INV-2025-0412', date: '12/04/2025', amount: '€99.00', status: 'paid' },
    { id: 'INV-2025-0312', date: '12/03/2025', amount: '€99.00', status: 'paid' },
    { id: 'INV-2025-0212', date: '12/02/2025', amount: '€99.00', status: 'paid' },
    { id: 'INV-2025-0112', date: '12/01/2025', amount: '€99.00', status: 'paid' },
    { id: 'INV-2024-1212', date: '12/12/2024', amount: '€99.00', status: 'paid' }
  ];

  // Calculate usage metrics from userInfo
  const usageMetrics = useMemo(() => {
    const bots = userInfo.bots || [];
    const connections = userInfo.connections || [];
    
    // Bot usage calculation
    const activeBots = bots.filter(bot => bot.active === true || bot.workflow_active === true).length;
    const totalBots = bots.length;
    const maxBots = 20; // Plan limit
    const botUsagePercentage = totalBots > 0 ? Math.round((activeBots / maxBots) * 100) : 0;
    
    // API usage calculation (based on tokens from activities)
    const totalTokensUsed = bots.reduce((sum, bot) => {
      if (!bot.activities) return sum;
      return sum + bot.activities.reduce((activitySum, activity) => {
        return activitySum + (activity.tokens || 0);
      }, 0);
    }, 0);
    const maxApiTokens = userInfo.credit_balance || 1000; // Use user's credit balance as limit
    const apiUsagePercentage = maxApiTokens > 0 ? 
      Math.min(Math.round((totalTokensUsed / maxApiTokens) * 100), 100) : 0;
    
    // Storage calculation (based on attachments)
    const totalStorageBytes = bots.reduce((sum, bot) => {
      if (!bot.attachments) return sum;
      return sum + bot.attachments.reduce((attachSum, attachment) => {
        return attachSum + (attachment.file_size || 0);
      }, 0);
    }, 0);
    
    const totalStorageGB = totalStorageBytes / (1024 * 1024 * 1024); // Convert to GB
    const maxStorageGB = 10; // Plan limit
    const storageUsagePercentage = Math.min(Math.round((totalStorageGB / maxStorageGB) * 100), 100);
    
    return {
      bots: {
        active: activeBots,
        total: totalBots,
        max: maxBots,
        percentage: botUsagePercentage
      },
      api: {
        used: totalTokensUsed,
        max: maxApiTokens,
        percentage: apiUsagePercentage
      },
      storage: {
        used: totalStorageGB,
        max: maxStorageGB,
        percentage: storageUsagePercentage
      }
    };
  }, [userInfo]);

  // Helper function to format numbers with thousands separator
  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-ES').format(num);
  };

  // Helper function to get progress bar color based on usage
  const getProgressColor = (percentage) => {
    if (percentage >= 90) return '#dc3545'; // Red - Critical
    if (percentage >= 75) return '#fd7e14'; // Orange - Warning
    if (percentage >= 50) return '#ffc107'; // Yellow - Caution
    return '#28a745'; // Green - Good
  };

  // Helper function for credit usage color (inverted logic)
  const getCreditProgressColor = (remainingPercentage) => {
    if (remainingPercentage <= 10) return '#dc3545'; // Red - Critical (low credits)
    if (remainingPercentage <= 25) return '#fd7e14'; // Orange - Warning
    if (remainingPercentage <= 50) return '#ffc107'; // Yellow - Caution
    return '#28a745'; // Green - Good
  };

  // Helper function to format file size
  const formatStorage = (gb) => {
    if (gb < 0.1) {
      return `${Math.round(gb * 1024)} MB`;
    }
    return `${gb.toFixed(1)} GB`;
  };
  
  useEffect(() => {
    try {
      // Safely retrieve userInfo from localStorage
      const userInfoString = localStorage.getItem('user_info');
      console.log('User info string:', userInfoString);
      
      if (userInfoString) {
        const userInfoData = JSON.parse(userInfoString);
        console.log('Parsed user info:', userInfoData);
        
        // Set the complete user info
        setUserInfo(userInfoData);
        
        // Check if userInfo and payment_methods exist
        if (userInfoData && userInfoData.payment_methods) {
          setPaymentMethods(userInfoData.payment_methods);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // Set to empty array if there's an error
      setPaymentMethods([]);
      setUserInfo({});
    }
  }, []);
  
  return (
    <div className="billing-container">
      <h1>Facturación</h1>
      
      <div className="billing-tabs">
        <button 
          className={`tab-btn ${activeTab === 'plan' ? 'active' : ''}`}
          onClick={() => setActiveTab('plan')}
        >
          Plan Actual
        </button>
        <button 
          className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Facturas
        </button>
        <button 
          className={`tab-btn ${activeTab === 'payment' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment')}
        >
          Métodos de Pago
        </button>
      </div>
      
      <div className="tab-content">
        {activeTab === 'plan' && (
          <div className="plan-tab">
            <div className="current-plan-card">
              <div className="plan-header">
                <h2>{currentPlan.name}</h2>
                <div className="plan-price">
                  <span className="price">{currentPlan.price}</span>
                  <span className="billing-cycle">/ {currentPlan.billingCycle}</span>
                </div>
              </div>
              
              <div className="plan-details">
                <p className="next-billing">
                  Próximo cargo: <strong>{currentPlan.nextBilling}</strong>
                </p>
                
                <div className="plan-features">
                  <h3>Características incluidas:</h3>
                  <ul>
                    {currentPlan.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="plan-actions">
                <button className="change-plan-btn">Cambiar Plan</button>
                <button className="cancel-plan-btn">Cancelar Suscripción</button>
              </div>
            </div>
            
            {/* Updated Usage Summary with Real Data */}
            <div className="usage-summary">
              <h3>Resumen de Uso</h3>
              <div className="usage-metrics">
                
                {/* Active Bots */}
                <div className="usage-metric">
                  <span className="metric-name">Bots Activos</span>
                  <div className="progress-bar">
                    <div 
                      className="progress" 
                      style={{ 
                        width: `${usageMetrics.bots.percentage}%`,
                        backgroundColor: getProgressColor(usageMetrics.bots.percentage)
                      }}
                    ></div>
                  </div>
                  <span className="metric-value">
                    {usageMetrics.bots.active} / {usageMetrics.bots.max}
                  </span>
                </div>

                {/* API Usage */}
                <div className="usage-metric">
                  <span className="metric-name">Uso de API</span>
                  <div className="progress-bar">
                    <div 
                      className="progress" 
                      style={{ 
                        width: `${usageMetrics.api.percentage}%`,
                        backgroundColor: getProgressColor(usageMetrics.api.percentage)
                      }}
                    ></div>
                  </div>
                  <span className="metric-value">
                    {formatNumber(usageMetrics.api.used)} / {formatNumber(usageMetrics.api.max)} tokens
                  </span>
                </div>

                {/* Storage Usage */}
                <div className="usage-metric">
                  <span className="metric-name">Almacenamiento</span>
                  <div className="progress-bar">
                    <div 
                      className="progress" 
                      style={{ 
                        width: `${usageMetrics.storage.percentage}%`,
                        backgroundColor: getProgressColor(usageMetrics.storage.percentage)
                      }}
                    ></div>
                  </div>
                  <span className="metric-value">
                    {formatStorage(usageMetrics.storage.used)} / {usageMetrics.storage.max} GB
                  </span>
                </div>

                {/* Additional metrics based on user data */}
                <div className="usage-metric">
                  <span className="metric-name">Conexiones</span>
                  <div className="progress-bar">
                    <div 
                      className="progress" 
                      style={{ 
                        width: `${Math.min((userInfo.connections?.length || 0) / 50 * 100, 100)}%`,
                        backgroundColor: getProgressColor(Math.min((userInfo.connections?.length || 0) / 50 * 100, 100))
                      }}
                    ></div>
                  </div>
                  <span className="metric-value">
                    {userInfo.connections?.length || 0} / 50
                  </span>
                </div>

                {/* Credit Balance - Now shows remaining vs total */}
                <div className="usage-metric">
                  <span className="metric-name">Créditos Restantes</span>
                  <div className="progress-bar">
                    <div 
                      className="progress" 
                      style={{ 
                        width: `${Math.min((userInfo.credit_balance || 0) / 1000 * 100, 100)}%`,
                        backgroundColor: getCreditProgressColor(Math.min((userInfo.credit_balance || 0) / 1000 * 100, 100))
                      }}
                    ></div>
                  </div>
                  <span className="metric-value">
                    {formatNumber(userInfo.credit_balance || 0)} / {formatNumber(1000)} créditos
                  </span>
                </div>

              </div>

              {/* Additional Usage Statistics */}
              <div className="usage-details">
                <div className="usage-detail-row">
                  <span className="detail-label">Total de Bots:</span>
                  <span className="detail-value">{usageMetrics.bots.total}</span>
                </div>
                <div className="usage-detail-row">
                  <span className="detail-label">Tokens utilizados:</span>
                  <span className="detail-value">{formatNumber(usageMetrics.api.used)}</span>
                </div>
                <div className="usage-detail-row">
                  <span className="detail-label">Archivos subidos:</span>
                  <span className="detail-value">
                    {userInfo.bots?.reduce((sum, bot) => sum + (bot.attachments?.length || 0), 0) || 0}
                  </span>
                </div>
                <div className="usage-detail-row">
                  <span className="detail-label">Contactos en rubrica:</span>
                  <span className="detail-value">
                    {userInfo.bots?.reduce((sum, bot) => sum + (bot.contacts?.length || 0), 0) || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'invoices' && (
          <div className="invoices-tab">
            <div className="invoices-header">
              <h2>Historial de Facturas</h2>
              <button className="download-all-btn">Descargar Todo</button>
            </div>
            
            <div className="invoices-table">
              <table>
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Fecha</th>
                    <th>Importe</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr key={invoice.id}>
                      <td>{invoice.id}</td>
                      <td>{invoice.date}</td>
                      <td>{invoice.amount}</td>
                      <td>
                        <span className={`status-badge ${invoice.status}`}>
                          {invoice.status === 'paid' ? 'Pagada' : 'Pendiente'}
                        </span>
                      </td>
                      <td>
                        <button className="invoice-action-btn">Ver</button>
                        <button className="invoice-action-btn">Descargar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {activeTab === 'payment' && (
          <div className="payment-tab">
            <div className="payment-header">
              <h2>Métodos de Pago</h2>
              <button className="add-payment-btn">+ Añadir Método de Pago</button>
            </div>
            
            <div className="payment-methods">
              {paymentMethods.length > 0 ? (
                paymentMethods.map(method => (
                  <div key={method.id} className={`payment-method-card ${method.is_default ? 'default' : ''}`}>
                    <div className="payment-method-icon">
                      {(method.payment_provider === 'stripe' || method.card_type) && (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                          <line x1="1" y1="10" x2="23" y2="10"></line>
                        </svg>
                      )}
                    </div>
                    <div className="payment-method-details">
                      <h4>
                        {method.card_type ? `${method.card_type} terminada en ` : 'Tarjeta terminada en '}{method.last4}
                        {method.is_default && <span className="default-badge">Predeterminado</span>}
                      </h4>
                      {method.expiry_month && method.expiry_year && (
                        <p className="expiry">Expira: {method.expiry_month}/{method.expiry_year}</p>
                      )}
                    </div>
                    <div className="payment-method-actions">
                      {!method.is_default && (
                        <button className="make-default-btn">Hacer Predeterminado</button>
                      )}
                      <button className="remove-method-btn">Eliminar</button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-payment-methods">
                  <p>No tienes métodos de pago configurados.</p>
                  <button className="add-first-payment-btn">Añadir Primer Método de Pago</button>
                </div>
              )}
            </div>
            
            <div className="billing-address">
              <div className="address-header">
                <h3>Dirección de Facturación</h3>
                <button className="edit-address-btn">Editar</button>
              </div>
              <div className="address-details">
                <p>{userInfo.name || 'Nombre no disponible'}</p>
                <p>{userInfo.email || 'Email no disponible'}</p>
                <p>Dirección de facturación pendiente de configurar</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Billing;